/**
 * Deploy markers — read hooks + config mutation.
 *
 * Backed by `apps/api/src/routes/deploy-markers.ts`. A deploy marker is a
 * per-site timestamp; the pre/post perf delta is computed at read time by
 * the `site_deploy_delta` Tinybird pipe (so `useDeployDelta` may 503 until
 * that pipe is deployed — callers should degrade gracefully).
 *
 * The secret is only ever present on the create/rotate (PATCH) response —
 * `useDeployConfig` (GET) never carries it. The reveal UI consumes the
 * mutation result directly; the query cache never stores the secret.
 */
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from '../api';
import { deployKeys } from './query-keys';

// ─── Types (mirror the route schemas) ────────────────────────────────────────

export interface DeployMarker {
  uuid: string; // `dpm-…`
  site_uuid: string;
  deploy_id: string | null;
  commit_sha: string | null;
  branch: string | null;
  deployer: string | null;
  provider: string | null;
  deploy_url: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface DeployConfig {
  site_uuid: string;
  enabled: boolean;
  /** Present only on create / rotate (PATCH) — never on the GET read. */
  secret?: string;
  webhook_url: string;
  default_ttl_hours: number;
  control_window_days: number;
  setup: { curl: string };
}

export interface DeployConfigPatchRequest {
  enabled: boolean;
  rotate_secret?: boolean;
  default_ttl_hours?: number;
  control_window_days?: number;
}

export interface DeployWebhookEvent {
  uuid: string; // `dwe-…`
  received_at: string;
  status_code: number;
  error_code: string | null;
  source_ip: string | null;
  raw_metadata: Record<string, unknown> | null;
}

export interface DeployDeltaMetrics {
  lcp: number | null;
  cls: number | null;
  inp: number | null;
  ttfb: number | null;
  fcp: number | null;
  performance_score: number | null;
}
export interface DeployDeltaPair {
  abs: number | null;
  pct: number | null;
}
export interface DeployDeltaResponse {
  deploy_marker_uuid: string;
  deploy_timestamp: string;
  pre_window: { start: string; end: string; sample_count: number; metrics: DeployDeltaMetrics };
  post_window: {
    start: string;
    end: string;
    is_partial: boolean;
    sample_count: number;
    metrics: DeployDeltaMetrics;
  };
  deltas: {
    lcp: DeployDeltaPair;
    cls: DeployDeltaPair;
    inp: DeployDeltaPair;
    ttfb: DeployDeltaPair;
    fcp: DeployDeltaPair;
    performance_score: DeployDeltaPair;
  };
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useSiteDeploys(
  siteUuid: string | null | undefined,
  days = 30,
): UseQueryResult<DeployMarker[]> {
  return useQuery({
    queryKey: deployKeys.list(siteUuid ?? '', days),
    queryFn: async () =>
      (await apiFetch<{ deploys: DeployMarker[] }>(`/v1/sites/${siteUuid}/deploys?days=${days}`))
        .deploys,
    enabled: !!siteUuid,
    // Best-effort card — if the backend is unavailable, degrade to the empty
    // state instead of retry-storming the console with failed requests.
    retry: false,
  });
}

export function useDeployConfig(siteUuid: string | null | undefined): UseQueryResult<DeployConfig> {
  return useQuery({
    queryKey: deployKeys.config(siteUuid ?? ''),
    queryFn: () => apiFetch<DeployConfig>(`/v1/sites/${siteUuid}/deploys/config`),
    enabled: !!siteUuid,
    retry: false,
  });
}

export function useUpdateDeployConfig(): UseMutationResult<
  DeployConfig,
  Error,
  { siteUuid: string; body: DeployConfigPatchRequest }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteUuid, body }) =>
      apiFetch<DeployConfig>(`/v1/sites/${siteUuid}/deploys/config`, { method: 'PATCH', body }),
    onSuccess: async (_data, vars) => {
      await qc.invalidateQueries({ queryKey: deployKeys.config(vars.siteUuid) });
    },
  });
}

export function useDeployAuditLog(
  siteUuid: string | null | undefined,
): UseQueryResult<DeployWebhookEvent[]> {
  return useQuery({
    queryKey: deployKeys.auditLog(siteUuid ?? ''),
    queryFn: async () =>
      (await apiFetch<{ events: DeployWebhookEvent[] }>(`/v1/sites/${siteUuid}/deploys/audit-log`))
        .events,
    enabled: !!siteUuid,
    retry: false,
  });
}

export function useDeployDelta(
  markerUuid: string | null | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<DeployDeltaResponse> {
  return useQuery({
    queryKey: deployKeys.delta(markerUuid ?? ''),
    queryFn: () => apiFetch<DeployDeltaResponse>(`/v1/deploy-markers/${markerUuid}/delta`),
    enabled: !!markerUuid && (options.enabled ?? true),
    // The delta pipe may be undeployed (503) — don't retry-storm; the
    // card just omits the delta sentence when this fails.
    retry: false,
  });
}
